import { jsx as _jsx } from "react/jsx-runtime";
import FocusTooltip from '@/components/atoms/FocusTooltip/FocusTooltip';
import Icon from '@/components/atoms/Icon/Icon';
import { dataLayerPush } from '@/helpers/dataLayer';
import { useSavedItems } from '@/hooks/useSavedItems';
import toast from 'react-hot-toast';
const SaveButton = ({ id, saveItemEndpoint = '/api-v12/saved-items', contentTypeId = 0, saveLabel = 'Save for later', removeLabel = 'Remove from saved', cookieName = 'saved_items', saveIcon = 'heart', activeSaveIcon = 'heart-solid', className, iconClassName, size = 'md', asDiv = false, tabIndex, iconClassNameActive, brandName, offer, articleTitle, componentName, eventName, containerClass, onSaveSuccess, }) => {
    const { isSaved, toggleItem } = useSavedItems(cookieName, saveItemEndpoint);
    const handleSaveClick = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.currentTarget.blur();
        if (!id)
            return;
        const wasSaved = isSaved(id);
        const result = await toggleItem(id, contentTypeId);
        if (!result.success && result.error) {
            toast.error(result.error, {
                duration: 5000,
                position: 'top-center',
                ariaProps: {
                    role: 'alert',
                    'aria-live': 'assertive',
                },
            });
        }
        else {
            const wishlistPayload = {
                wishlist_type: componentName ?? 'Saved Items Listing',
                wishlist_interaction: wasSaved ? 'Remove' : 'Save',
            };
            if (brandName)
                wishlistPayload.brand_name = brandName;
            if (offer)
                wishlistPayload.offer = offer;
            if (articleTitle)
                wishlistPayload.article_title = articleTitle;
            if (eventName)
                wishlistPayload.centre_event_name = eventName;
            dataLayerPush('wishlist', wishlistPayload);
            // Call onSaveSuccess callback if provided
            if (onSaveSuccess) {
                try {
                    await onSaveSuccess();
                }
                catch (error) {
                    console.error('❌ Error in onSaveSuccess callback:', error);
                }
            }
        }
    };
    const isItemSaved = id ? isSaved(id) : false;
    if (!id)
        return null;
    const sizeClasses = {
        md: 'w-[24px]',
        lg: 'w-[28px]',
        xl: 'md:w-[36px] w-[28px]',
    };
    const iconClasses = `text-symbol-muted hover:text-symbol-brand aspect-square transition-all duration-500 ease-out ${sizeClasses[size]} ${iconClassName || ''}`;
    const activeIconClasses = `text-symbol-brand aspect-square transition-all duration-500 ease-out  ${sizeClasses[size]} ${iconClassNameActive || ''}`;
    const baseClasses = `min-h-[24px] min-w-[24px]  transition-all duration-300 ease-out hover:scale-110 hover:rotate-6 active:scale-125 active:rotate-0 ${className || ''} cursor-pointer `;
    const commonProps = {
        onClick: handleSaveClick,
        'aria-label': articleTitle
            ? `${isItemSaved ? removeLabel : saveLabel}: ${articleTitle}`
            : isItemSaved
                ? removeLabel
                : saveLabel,
        'data-testid': 'save-button',
        className: baseClasses,
    };
    const content = !isItemSaved ? (_jsx(Icon, { className: iconClasses, variant: saveIcon })) : (_jsx(Icon, { className: activeIconClasses, variant: activeSaveIcon }));
    const name = `save-button-${id}-${contentTypeId}`;
    const contentLabel = isItemSaved ? removeLabel : saveLabel;
    const commonPropsTooltip = {
        name,
        content: contentLabel,
        position: componentName === 'Article Card Carousel' ? 'left' : 'auto',
        ariaContent: null,
        isPolite: false,
    };
    if (asDiv) {
        return (_jsx("div", { className: containerClass, children: _jsx(FocusTooltip, { ...commonPropsTooltip, children: (tooltipProps) => (_jsx("div", { ...commonProps, ...tooltipProps, role: "button", tabIndex: tabIndex !== undefined ? tabIndex : 0, "aria-label": isItemSaved ? removeLabel : saveLabel, onKeyDown: (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleSaveClick(e);
                        }
                    }, children: content })) }) }));
    }
    return (_jsx("div", { className: containerClass, children: _jsx(FocusTooltip, { ...commonPropsTooltip, children: (tooltipProps) => (_jsx("button", { tabIndex: tabIndex !== undefined ? tabIndex : 0, ...commonProps, ...tooltipProps, children: content })) }) }));
};
export default SaveButton;
